import React from "react";
import GetStartedSection from "../components/GetStartedSection";
import Navbar from "../components/Navbar";
import StatsSection from "../components/StatsSection";
import FeaturesSection from "../components/FeaturesSection";
import useFunnelNavigation from "../hooks/useFunnelNavigation";
import { useTrackingCapture } from "../hooks/useTrackingCapture";
import TestimonialsSection from "../components/TestimonialsSection";
import AchievementsSection from "../components/AchievementsSection";
import JobSearchResults from "../components/JobSearchResults";
import ericaImg from "../assets/erica.png";
import haileyImg from "../assets/hailey.png";
import jaylenImg from "../assets/jaylen.png";
import donaldImg from "../assets/donald.png";
import camilaImg from "../assets/camila.png";
import monicaImg from "../assets/monica.png";
import chenImage from "../assets/chen.png";
import calendarIcon from "../assets/calendar.svg";
import stopwatchIcon from "../assets/stopwatch.svg";
import diagramIcon from "../assets/diagram.svg";
import thumbsUpIcon from "../assets/thumbs_up.svg";
import heroRightDesktop from "../assets/slider_desktop.png";
import heroRightMobile from "../assets/slider_mobile.png";
import nvidiaImg from "../assets/nvidia.webp";
import morganImg from "../assets/morgan.png";
import applicationImg from "../assets/application.webp";
import interviewImg from "../assets/interview.png";
import { getBrand } from "../utils/getBrand";
import HeroSectionSlider from "../components/HeroSectionSlider";
import useMobile from "../helpers/useMobile";

const Landing = () => {
  const { navigateToQuiz, navigateToCheckout } = useFunnelNavigation();
  const { brandName } = getBrand();
  const isMobile = useMobile(478);
  // Capture tracking data on home page load
  useTrackingCapture();
  const features = [
    {
      title: "Get matched with the best-fit jobs you’re most likely to land",
      description:
        "In seconds, we scan thousands of job boards and career pages to find open opportunities that match your salary goals, experience, and other preferences.",
      comparisons: [
        { text: "Find 20x more relevant jobs", type: "green" },
        { text: "Cut search time by 90%", type: "green" },
      ],
      image: nvidiaImg,
    },
    {
      title: "We build your resume and tailor it perfectly for every application",
      description:
        "Our system builds you a professional-grade resume and personalizes it for any job — perfectly matching the role’s requirements and maximizing your chances of landing an interview.",
      comparisons: [
        { text: "Get 10x more interviews", type: "blue" },
        { text: "Land interviews 5x faster", type: "blue" },
      ],
      image: morganImg,
    },
    {
      title: "Auto-apply to countless jobs instantly",
      description:
        "Effortlessly auto-apply to tons of companies with one click and save hours of busywork—all tracked and managed in an automated dashboard.",
      comparisons: [
        { text: "Send 50x more applications", type: "purple" },
        { text: "Cut application time by 95%", type: "purple" },
      ],
      image: applicationImg,
    },
    {
      title: "Ace any interview and negotiation",
      description:
        "Prepare with a personalized AI coach—every simulation is tailored to the exact job you’re applying for. Get real-time feedback, personalized answer suggestions, and a custom cheat sheet.",
      comparisons: [
        { text: "Get 5x more job offers", type: "yellow" },
        { text: "Get the highest offer possible", type: "yellow" },
      ],
      image: interviewImg,
    },
  ];
  const testimonials = [
    {
      name: "Erica T., 35",
      role: "Accountant",
      text: "Honestly, it felt like having a personal recruiter. I went from zero responses to multiple interviews in just a few weeks and ended up with several options. Wish I’d had this sooner!",
      verified: true,
      image: ericaImg,
    },
    {
      name: "Hailey M., 32",
      role: "Social media manager",
      text: "I used to spend over 30 hours a week applying manually and hearing nothing. Once I switched to this, interviews started coming in almost immediately. The personalized resumes made a huge difference.",
      verified: true,
      image: haileyImg,
    },
    {
      name: "Jaylen R., 27",
      role: "Software engineer",
      text: "This is nuts. I used to spend hours applying to like five jobs. Then suddenly, I was sending out hundreds automatically, getting interviews nonstop, and companies were literally competing for me. What??",
      verified: true,
      image: jaylenImg,
    },
    {
      name: "Donald H., 47",
      role: "Copywriter",
      text: "I had no idea how many great jobs I was missing until I tried . It found openings I would’ve never come across on my own—and one of them turned out to be the perfect fit. I’m so happy! I love my new job!",
      verified: true,
      image: donaldImg,
    },
    {
      name: "Camila H., 24",
      role: "Data analyst",
      text: "I always thought job hunting had to be stressful, but this changed my mind. Everything from applying to prep felt smooth! The interview coaching was my favorite—it felt like having insider info.",
      verified: true,
      image: camilaImg,
    },
    {
      name: "Monica J., 43",
      role: "Account manager",
      text: "I always had a feeling, but I never realized just how underpaid I was at my last job. This helped me prove my value, ace interviews, and walk away with a far better deal. A total lifesaver.",
      verified: true,
      image: monicaImg,
    },
  ]
  return (
    <>
      <Navbar 
        showNavigation={false}
        showSignIn={true}
        showGetStarted={true}
      />
      <main id="main-content">
			<HeroSectionSlider
				content={{
					avatarText: isMobile ? (
						<div className="text-[14px]/[22px]">
							<span className="font-semibold">10,500+</span> people found jobs last month
						</div>
					) : (
						<>
							<span className="font-semibold">10,500+</span> people found new jobs last month
						</>
					),
					textOne: 'Get hired 3x faster. Hassle-free.',
					textTwo: null,
					textThree: 'Don’t wait—land your dream job and salary with zero stress:',
					image: heroRightDesktop,
					imageMobile: heroRightMobile,
				}}
				modifiers={{
					textThreeStyles: {
						color: 'black',
					},

					textOneStyle: {
						fontSmall: true,
					},
				}}
				displayChecklist={true}
				checklistContent={[
					'Perfectly matched jobs you’re most likely to land',
					'Resumes and cover letters tailored to each role',
					'Jobs auto-applied for you',
					'Interviews aced with a live AI coach',
				]}
				firstButtonText="Get my job matches now"
				showSecondButton={false}
			/>
      <StatsSection stats={[
        {
          value: "150k+",
          label: "Users found their dream job",
        },
        {
          value: "30 days",
          label: "Average time to first job offer",
        },
        {
          value: "+32%",
          label: "Average pay jump",
        },
        {
          value: "40h+",
          label: "Saved every month",
        },
      ]}
      color="green"
      title="Our users are living proof that it works"
      />
      <FeaturesSection 
        showFirstButton={true}
        showSecondButton={true}
        firstButtonText="Get my job matches now"
        secondButtonText={`Get started with ${brandName}`}
        onFirstButtonClick={() => navigateToQuiz(1)}
        onSecondButtonClick={() => navigateToCheckout()} 
        customfeatures={features}
        title="Get job offers on autopilot"
        subtitle="We automate job hunting and help you at every step — so you can land your dream job with minimal time and effort"
      /> 
      <TestimonialsSection
        showFirstButton={true}
        showSecondButton={true}
        firstButtonText="Get my job matches now"
        secondButtonText={`Get started with ${brandName}`}
        onFirstButtonClick={() => navigateToQuiz(1)}
        onSecondButtonClick={() => navigateToCheckout()} 
        testimonials={testimonials}
      />
      <AchievementsSection />
      <JobSearchResults content={{
        title: "What happens when job search is done right",
        description: "Faster offers, bigger salaries, and happier jobs — here’s what users are getting.",
        numbersTitle: "By the Numbers",
        numbers: [
          {
            title: "Time to a new job",
            value: "27 days",
            icon: calendarIcon,
          },
          {
            title: "Total time invested",
            value: "5 hours",
            icon: stopwatchIcon,
          },
          {
            title: "Salary increase from last job",
            value: "32%",
            icon: diagramIcon,
          },
          {
            title: "Satisfaction rate",
            value: "95%",
            icon: thumbsUpIcon,
          },
        ],
        testimonial: {
          name: "Emily Chen",
          role: "Data scientist",
          text: "You guys said it could happen fast, but that fast? In less than a month, I had multiple offers from tech companies—and I’d barely put in any time. They were all great, but I’d always dreamed of working at Netflix, so I gladly accepted. Thank you!",
          rating: "4.5 out of 5 stars",
          image: chenImage,
          showSalaryIncrease: false,
        }
  }} />
      <GetStartedSection showGetStarted={true} />
      </main>
    </>
  );
};

export default Landing;
